import * as THREE from 'three';
import { clamp, hash01, smoothstep } from '@/core/rng';
import { surfaceHeight } from '@/world/chunk';
import { skyColourAlong } from '@/render/sky';
import type { Region } from '@/world/region';
import type { Landmark } from '@/world/landmarks';

/**
 * Waterfalls, as something you can see from the next valley (Design III §15).
 *
 * `world/landmarks.ts` found 161 of them and `tools/landmarks.ts` said in its
 * own output why that changed nothing on a screen: a fall was 120 m of
 * terrain-coloured river, 42 px at three kilometres, drawn in exactly the
 * greens and browns of the hillside it runs down.
 *
 * The measurement that shaped this is that **size was never the problem**. The
 * drops are large — p50 118 m, p90 461, one of 1,930 — so a median fall is
 * already 42 px tall at 3 km, well over the ~3 px bar every other mark in this
 * game is held to. What it lacked was CONTRAST. So this adds no plume and no
 * icon; it makes the water white, which is what falling water is.
 *
 * Two things follow from measuring rather than guessing:
 *
 * - **A fall draws its own water.** `render/rivers.ts` only lays a ribbon
 *   above `VISIBLE_FLOW` (620), and waterfall flow is p50 92 — so most falls
 *   are on channels with no drawn river at all. Big drops and big discharge
 *   are anti-correlated (a large river has eroded its bed; the 1,930 m plunges
 *   are threads down cliffs), and gating on the river would have thrown away
 *   every dramatic one. A thread of white down a cliff is a real thing and it
 *   does not need a visible river above it — at 260 m a cell, the channel
 *   feeding it is sub-pixel anyway.
 *
 * - **It is lit like water, not tinted like ground.** `rivers.ts` learned this
 *   the hard way: without an emissive floor a shaded reach goes to ground-grey
 *   and vanishes against its own damp margin. Falling water is aerated and is
 *   the brightest thing on a hillside; the emissive here is stronger than the
 *   river's for that reason.
 *
 * Render-only, and built once from two immutable inputs. `update` advances one
 * time uniform and re-aims the mist; nothing here is ever read by the world.
 */

export interface Falls {
  group: THREE.Group;
  /**
   * `tSec` drives the fall's run; `eye` aims the mist. `viewportPxH` and
   * `fovDeg` size the sub-pixel fade — the drawing buffer's height in device
   * pixels and the camera's vertical fov. Both are optional: without them the
   * fade assumes `window.innerHeight` at the renderer's default pixel ratio
   * (`min(devicePixelRatio, 2)`, main.ts:100) and 46°, which is right unless
   * the resolution slider has been moved; hand it
   * `renderer.getDrawingBufferSize(v).y, camera.fov` to make it exact.
   */
  update(tSec: number, eye: THREE.Vector3, viewportPxH?: number, fovDeg?: number): void;
  /**
   * The landmark ids that actually carry white water on the DRAWN ground —
   * a fall whose whole reach is a hillside draws nothing (see `WHITE_LO`),
   * and anything that steers an eye toward "a waterfall" should prefer these.
   */
  readonly drawnIds: ReadonlySet<string>;
  dispose(): void;
}

/** How wide the white water runs, from accumulated flow. A fall constricts:
 *  narrower than `rivers.ts` gives the same channel, never wider. */
const widthOf = (flow: number) => clamp(Math.log(flow / 60 + 1) * 4.2, 3, 22);

/*
 * THE STREAKS, and what they were (2026-08-15).
 *
 * Every screenshot at every altitude carried thin white diagonal streaks
 * across the terrain, and elimination in the live page — hiding one scene
 * group at a time — left them standing with curtains, rain, structures,
 * trails, litter, people, rivers, the sea and the horizon all hidden, and
 * removed them with only THIS mesh hidden (the mist stayed on). They were the
 * falls layer working exactly as written, and three things about how it was
 * written made a waterfall read as an artifact. Measured on the shipped
 * island's 161 falls (`__terra.groundAt`, the surface everything stands on):
 *
 * - **Dashed.** The ribbon was seated at 14 rungs per reach — one every 19–26
 *   m — over a ground lattice of 4 m, on weak channels where `surfaceHeight`
 *   damps none of its fine noise. Between rungs the straight-interpolated
 *   ribbon ran UNDER the drawn ground for p50 8% / p90 20% of its length
 *   (28.6% on the fall nearest the opening camp, up to 3.1 m under), so what
 *   showed was the pieces that poked through: a dashed line.
 * - **On slopes, not falls.** `world/landmarks.ts` scores a fall as flow ×
 *   drop with a floor of 3,000, so a big river dropping five metres qualifies:
 *   drop p10 / p50 / p90 was 4.8 / 86 / 398 m over a 260–368 m reach, and 51
 *   of the 161 had an overall grade under 0.15 (8.5°). The old taper,
 *   `smoothstep(0.10, 0.42, grade)`, still gave an 11° brook a 0.4 m white
 *   thread — a hairline, but an emissive one, drawn straight down a hillside.
 * - **Hairlines everywhere.** `widthOf` floors at 3 m and the taper only
 *   narrows, so 150 of 161 falls were under 2 m wide at their WIDEST — sub-
 *   pixel from ~600 m and 0.85 px at 3 km, and `frustumCulled = false` with an
 *   always-on emissive submits all of them at any altitude. A sub-pixel bright
 *   line is exactly an aliasing streak.
 *
 * So the ribbon is now (1) seated every `RUNG` metres on the drawn surface,
 * sliding sideways into the local gutter, so it never sinks and is not a
 * ruler line; (2) white only where a 24 m-window grade says the water is
 * actually falling, with a hard zero below `WHITE_LO` so a slope draws
 * nothing and a reach with no white at all is not emitted; and (3) faded in
 * the shader as its projected width drops through a pixel, so a distant
 * thread thins away instead of glowing. Dense sampling costs 1.5 µs a
 * `surfaceHeight` — the whole island's falls seat in ~20 ms at build.
 */
/** Rung spacing down the reach — the ground's own lattice, so no dash. */
const RUNG = 4;
/** Half-window, in rungs, of the grade the whiteness reads (±12 m = 24 m):
 *  the fine grade at 4 m is hummock noise (p50 0.78 on this island) and
 *  would whiten every knoll; the reach's coarse grade is what a fall IS. */
const GRADE_HALF = 3;
/**
 * Where white water begins and where it is full, as grade. Below `WHITE_LO`
 * the width is exactly zero. 0.30 is 17° — the steepest a brook runs before
 * it breaks white — and 0.75 is 37°, past which it is simply falling.
 * Measured on the land's slope with the 24 m window, over the shipped
 * island: **101 of 161 falls draw**, 95 of them as ONE continuous run
 * (longest run p10 / p50 / p90 = 104 / 264 / 372 m; drop 61 / 169 / 458 m;
 * width p50 3.7 m), and the sixty that draw nothing are the reaches whose
 * whole run is a hillside — drop p10 4.8 m, grade under 0.15.
 */
const WHITE_LO = 0.30, WHITE_HI = 0.75;
/** How far, in metres, the thread may slide sideways to find the gutter. */
const WANDER = 6;
/**
 * A fall is drawn only if its white water runs at least this far without a
 * break. Measured after the land-grade rule: 111 falls carried white, 103 of
 * them in ONE continuous run (p50 244 m), and the ten under 40 m were all
 * under 0.1 m wide — slivers at the threshold that would draw as a 40-pixel
 * dash from 300 m if they drew at all. Ten rungs.
 */
const MIN_RUN = 10;
/** Mist puffs, total, across every fall in sight. */
const MIST_CAP = 700;
/**
 * Past this the mist is not drawn at all, and the white ribbon carries the
 * fall on its own.
 *
 * The first cut borrowed §10's hearth-smoke rule — grow the puff with its own
 * distance so its APPARENT size holds — and that was the wrong rule borrowed
 * from the right place. Smoke is deliberately outsized because its whole job
 * is to be seen from eight kilometres; spray is a detail that says *water*
 * when you are near enough to hear it. Grown that way it reached 195 m a puff
 * at 3 km and fourteen of them overlapped into a flat white wedge across a
 * third of the frame, washing the hillside out behind it. Mist is real-size
 * now and simply stops.
 */
const MIST_RANGE = 1100;
const LIFT = 0.7;              // clears the drawn facets, as rivers.ts does

export function buildFalls(region: Region, marks: readonly Landmark[]): Falls {
  const group = new THREE.Group();
  const { n, cell } = region;
  // The Region's own cell centre (2026-08-19). It was `(i + 0.5)·cell − half`
  // — the cell EDGE in the convention `gridOf` below and `Region.nearest`
  // share (`round(wx / cell + n / 2)`), so every fall's reach was drawn 130 m
  // east and 130 m south of the channel the flow field routed and the
  // terrain carved: down the hillside BESIDE its gully. `rivers.ts` had the
  // same line and the same 184 m; see the note under VISIBLE_FLOW there.
  const centre = (i: number) => (i - n / 2) * cell;
  const gridOf = (wx: number, wz: number) => {
    const x = Math.round(wx / cell + n / 2), z = Math.round(wz / cell + n / 2);
    return z * n + x;
  };

  const falls = marks.filter((m) => m.kind === 'waterfall');

  // ---- the white water: one ribbon per fall, built once -------------------
  const positions: number[] = [];
  /** Half-width at each vertex, metres — the shader fades a thread out as
   *  this drops below a pixel on screen. */
  const halves: number[] = [];
  /** −1 on one edge of the white water, +1 on the other: the shader keeps
   *  the core solid and feathers the edges into spray. */
  const acrosses: number[] = [];
  const indices: number[] = [];
  /** Where each fall lands, and how hard — the mist reads these. */
  const feet: { x: number; y: number; z: number; power: number; seed: number }[] = [];
  const drawnIds = new Set<string>();

  for (const m of falls) {
    const i = gridOf(m.x, m.z);
    const d = region.down[i];
    if (d < 0) continue;
    const ax = centre(i % n), az = centre((i / n) | 0);
    const bx = centre(d % n), bz = centre((d / n) | 0);
    const flow = region.siteAt(ax, az).flow;
    const hw = widthOf(flow) / 2;

    const dx = bx - ax, dz = bz - az;
    const dl = Math.hypot(dx, dz) || 1;
    // lateral unit — the ribbon's own width runs along it, and so does the
    // sideways slide into the gutter
    const nx = -dz / dl, nz = dx / dl;
    const N = Math.max(2, Math.ceil(dl / RUNG));
    const step = dl / N;

    // Walk the reach at the lattice spacing, seating every rung on the drawn
    // surface and letting the thread slide toward the lowest of three lateral
    // samples — low-passed, so it wanders like water and does not zigzag on
    // the noise. `py` is the drawn ground WITHOUT the lift; `pl` is the LAND —
    // the smooth bicubic field the fall was found in — and the whiteness reads
    // that, because the drawn surface carries ±2.5 m of hummock on top and a
    // grade read through it flickers across the threshold every few rungs
    // (measured: p50 2 white patches a fall, p90 4 — dashes again, from a
    // different cause). White where the land falls; seated where the ground is.
    const px: number[] = [], pz: number[] = [], py: number[] = [], pl: number[] = [];
    let off = 0;
    for (let k = 0; k <= N; k++) {
      const t = k / N;
      const cx = ax + dx * t, cz = az + dz * t;
      let best = 0, bestH = Infinity;
      for (const o of [-WANDER, 0, WANDER]) {
        const h = surfaceHeight(region, cx + nx * o, cz + nz * o, 1);
        if (h < bestH) { bestH = h; best = o; }
      }
      off = off * 0.72 + best * 0.28;
      const x = cx + nx * off, z = cz + nz * off;
      px.push(x); pz.push(z);
      py.push(surfaceHeight(region, x, z, 1));
      pl.push(region.heightAt(x, z));
    }

    // White water is what STEEP water looks like — and only that. Drawn at
    // full width down the whole reach, the flat run-out at the bottom lay on
    // level ground as an opaque band and photographed as a painted road
    // stripe; tapered by grade but never to nothing, an 11° hillside carried
    // a hairline. So the width is a hard zero below `WHITE_LO`, read over a
    // 24 m window of the land's own slope.
    let prev = -1;
    let anySteep = false;
    let footK = N, run = 0, longest = 0;
    // no fall holds one width down its length: a slow ±22% swell along the
    // run, phased per fall, so it reads as water finding its way and not as
    // a stripe of even paint
    const phase = hash01(i, 0, 0x5a11) * Math.PI * 2;
    for (let k = 0; k <= N; k++) {
      const a = pl[Math.max(0, k - GRADE_HALF)], b = pl[Math.min(N, k + GRADE_HALF)];
      const span = (Math.min(N, k + GRADE_HALF) - Math.max(0, k - GRADE_HALF)) * step;
      const grade = (a - b) / (span || 1);
      const steep = smoothstep(WHITE_LO, WHITE_HI, grade);
      const swell = 1 + 0.22 * Math.sin(k * 0.37 + phase);
      const w = hw * steep * swell;
      if (w > 0) { footK = k; run++; longest = Math.max(longest, run); } else run = 0;
      if (steep > 0.35) anySteep = true;
      const ox = nx * w, oz = nz * w;
      const y = Math.max(py[k] + LIFT, 0.55);
      const base = positions.length / 3;
      positions.push(px[k] - ox, y, pz[k] - oz, px[k] + ox, y, pz[k] + oz);
      halves.push(w, w);
      acrosses.push(-1, 1);
      // wound to face up — rivers.ts spent a session on exactly this bug
      if (prev >= 0) indices.push(prev, prev + 1, base, prev + 1, base + 1, base);
      prev = base;
    }
    // A reach with no white run worth the name is a river, not a fall: it
    // draws nothing and there is no mist under it. (The vertices already
    // pushed are zero-width — degenerate, never rasterised — but drop them.)
    if (longest < MIN_RUN) {
      positions.length -= (N + 1) * 6;
      halves.length -= (N + 1) * 2;
      acrosses.length -= (N + 1) * 2;
      indices.length -= N * 6;
      continue;
    }
    drawnIds.add(m.id);
    // the mist lands where the white water ENDS, not at the cell centre
    if (anySteep) {
      feet.push({ x: px[footK], y: Math.max(py[footK] + LIFT, 0.55), z: pz[footK],
                  power: m.score, seed: (i * 2654435761) >>> 0 });
    }
  }

  const time = { value: 0 };
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geo.setAttribute('aHalf', new THREE.BufferAttribute(new Float32Array(halves), 1));
  geo.setAttribute('aAcross', new THREE.BufferAttribute(new Float32Array(acrosses), 1));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  geo.computeBoundingSphere();

  const mat = new THREE.MeshStandardMaterial({
    // aerated water is white, not blue — the blue of a river is depth, and a
    // fall has none. This is the whole fix: `rivers.ts`' 0x74aac2 on a shaded
    // slope is the hillside's own value, which is why 161 falls were invisible.
    color: 0xdfeef5, roughness: 0.3, metalness: 0.0,
    // …and the emissive is written every frame by `update`, scaled by how much
    // sky there actually is. The value here is only what the first frame uses,
    // before `paintSky` has ever run. See SKY_BRIGHT below for why.
    emissive: 0x8fb6c8, emissiveIntensity: 0.85,
    transparent: true, opacity: 0.94, depthWrite: false,
    side: THREE.DoubleSide,
  });
  // Pixels per metre at unit view depth: half the viewport height times the
  // projection's vertical scale (1 / tan(fov/2)). `update` refreshes it from
  // the drawing buffer's height each frame, so a resize cannot stale it.
  const pxPerM = { value: 360 * 2.4 };
  /*
   * DEPTH BIAS, as a fraction of view distance. The ribbon is seated on the
   * TRUE surface — the 4 m lattice everything stands on — but a chunk two
   * kilometres off is drawn at a 16–32 m lattice whose planar facets sit
   * metres off the true ground in every hollow (§34: LOD 3's bilinear gap is
   * p90 1.6 m, worst 5.3; LOD 2 worst 10.2), the gutter the thread slides
   * into is a hollow by construction, and at 3 km a 24-bit depth buffer with
   * near 0.1 resolves only ~5 m. So each vertex is pulled toward the eye by
   * this fraction of its own distance before projection: screen position is
   * untouched (the perspective divide cancels a uniform scale of view space),
   * only depth moves — 12 m at 3 km, 1.2 m at 300 m. Measured on the Black
   * Fall (rare, a 252 m drop) from 2,907 m downhill, framebuffer read back
   * inside `renderer.render`, falls on minus falls off at > 30 luminance:
   * **128 pixels with no bias, 146 with this one**, 89 → 92 of the thread's
   * rows lit — the eye reads that as a dashed thread becoming a continuous
   * one. Anything else within that sliver of the sight line is overdrawn; a
   * trunk on the bank 1 m in front of falling water is the whole exposure at
   * play range.
   *
   * And a trap for the next person who measures this: from ACROSS its own
   * ridge — the eye perpendicular to the run and 40° above it — the same fall
   * showed 5 pixels with the depth test on and 82 with it off, which reads as
   * burial and is not. A 35° face seen edge-on is behind its ridge, and no
   * bias short of drawing through the mountain (0.03 was tried) reveals it.
   * Occlusion is not a bug; measure a fall from where its face looks at you.
   */
  const DEPTH_BIAS = 0.004;
  // the run: a scrolling streak down the fall's own length, world-anchored so
  // it does not swim when the camera moves
  mat.onBeforeCompile = (sh) => {
    sh.uniforms.uFall = time;
    sh.uniforms.uPxPerM = pxPerM;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>',
        '#include <common>\nattribute float aHalf;\nattribute float aAcross;\nuniform float uPxPerM;\nvarying vec3 vFallW;\nvarying float vFallPx;\nvarying float vFallX;')
      .replace('#include <project_vertex>',
        `#include <project_vertex>
  mvPosition.xyz *= ${(1 - DEPTH_BIAS).toFixed(4)};
  gl_Position = projectionMatrix * mvPosition;`)
      .replace('#include <worldpos_vertex>',
        `#include <worldpos_vertex>
  vFallW = (modelMatrix * vec4(transformed, 1.0)).xyz;
  vFallX = aAcross;
  // the ribbon's full width in screen pixels at this vertex
  vFallPx = aHalf * 2.0 * uPxPerM / max(-mvPosition.z, 1.0);`);
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>',
        '#include <common>\nuniform float uFall;\nvarying vec3 vFallW;\nvarying float vFallPx;\nvarying float vFallX;')
      // The run (2026-08-19): two streak frequencies falling at two speeds,
      // their phase wandering across the width, so the white is ropes of
      // water and not one sine; a solid core and edges feathered into spray
      // (`vFallX`), so the ribbon has no cut edge against the rock.
      .replace('#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
         float ph = sin(vFallW.x * 0.21 + vFallW.z * 0.19) * 2.0 + vFallX * 1.7;
         float s1 = sin(vFallW.y * 0.55 - uFall * 3.1 + ph);
         float s2 = sin(vFallW.y * 1.70 - uFall * 5.3 + ph * 1.6 + vFallX * 3.0);
         float streak = 0.6 * s1 + 0.4 * s2;
         float core = 1.0 - smoothstep(0.45, 1.0, abs(vFallX));
         totalEmissiveRadiance *= (0.80 + 0.36 * streak) * (0.72 + 0.28 * core);`)
      // A thread much narrower than a pixel is not a thin waterfall on screen,
      // it is an aliasing streak — MSAA resolves a 0.3 px emissive line to a
      // flickering 30% white. So the ribbon fades out below a pixel: full at
      // 1 px (a 3 m fall at 2.5 km), gone at 0.3 (the same fall at 8 km).
      // (`diffuseColor` is what `opacity` was multiplied into, so scaling its
      // alpha here is the same knob.)
      .replace('#include <opaque_fragment>',
        `diffuseColor.a *= smoothstep(0.3, 1.0, vFallPx)
                         * (0.55 + 0.45 * (1.0 - smoothstep(0.55, 1.0, abs(vFallX))));
         #include <opaque_fragment>`);
  };
  const mesh = new THREE.Mesh(geo, mat);
  mesh.name = 'falls';
  mesh.frustumCulled = false;
  group.add(mesh);

  // ---- the mist at the foot ----------------------------------------------
  // Camera-facing, because an up-facing pancake is nothing seen edge-on — the
  // lesson §10's smoke column had to learn twice. And SOFT: the first cut used
  // a flat-coloured plane and photographed as a hard white square pasted on
  // the hillside, which is the exact failure that got promotion's ground mark
  // parked. A puff needs a radial falloff or it is a decal.
  const mist = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({
      map: mistTexture(), color: 0xf2f8fb,
      transparent: true, opacity: 0.5,
      depthWrite: false, side: THREE.DoubleSide,
    }),
    MIST_CAP);
  mist.name = 'fall-mist';
  mist.frustumCulled = false;
  mist.count = 0;
  mist.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(mist);

  const _m = new THREE.Matrix4(), _p = new THREE.Vector3();
  const _q = new THREE.Quaternion(), _s = new THREE.Vector3();

  /*
   * HOW MUCH SKY THERE IS, as one number, and why both surfaces need it.
   *
   * Two things in this file emit light that no light source gave them. The
   * fall's `emissive` is added unconditionally — Three computes
   * `outgoingLight = diffuse + specular + emissive` — and the mist is a
   * `MeshBasicMaterial`, which is not lit at all and simply draws its colour.
   * Both were tuned in daylight, and both keep every drop of it at midnight:
   * measured against `rivers.ts`' floor, the fall's emissive is about **10x**
   * the river's in linear radiance, and the river was already 3.5x the ground
   * it stood on at 00:28.
   *
   * (`grep -n emissive src/render/*.ts` finds only this file and rivers.ts, so
   * the class is closed by these two commits — but note the mist is a THIRD
   * instance that the grep does NOT find, because an unlit material needs no
   * emissive to glow in the dark.)
   *
   * The fix is one scalar rather than a per-channel sky tint, on purpose. The
   * falls' near-white `0xdfeef5` is the "aerated water is white, not blue"
   * read that this file was written to win, and multiplying it channel-wise by
   * a low sun would trade that for an orange fall. So the HUE is held exactly
   * as shipped and only the BRIGHTNESS follows the sky. Sunset tinting is the
   * available refinement and is deliberately not taken here.
   *
   * `MIDDAY_SKY_LUM` is the linear luminance of the same sky sample at the
   * canonical clear midday (t = 3153904128), so the ratio is exactly 1.000
   * under a CLEAR sky and daylight cannot move.
   *
   * **Under cloud it is deliberately not 1.000, and that is not drift.**
   * `paintSky` is handed the overcast at the presence's own position, so the
   * dome is a local reading — measured at this fall on the same clock,
   * overcast 0.466 gives 1.057 where the river site's overcast 0.000 gives
   * 1.000. A grey lid scatters more light into the 40-degree band than a clear
   * sky does, so an overcast fall is slightly brighter. That is the right way
   * round, and it is written here because the next person to measure this over
   * a cloudy valley will otherwise read 1.057 as a broken constant.
   */
  const SKY_SAMPLE_EL = 40 * Math.PI / 180;
  const skyDir = new THREE.Vector3(0, Math.sin(SKY_SAMPLE_EL), -Math.cos(SKY_SAMPLE_EL));
  const skyCol = new THREE.Color();
  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const MIDDAY_SKY_LUM = 0.187438;
  const FALL_EMISSIVE = new THREE.Color(0x8fb6c8);
  const MIST_COLOUR = new THREE.Color(0xf2f8fb);
  const mistMat = mist.material as THREE.MeshBasicMaterial;

  return {
    group,
    drawnIds,
    update(tSec, eye, viewportPxH, fovDeg) {
      time.value = tSec;
      const viewH = viewportPxH ?? (typeof window !== 'undefined'
        ? window.innerHeight * Math.min(window.devicePixelRatio || 1, 2) : 720);
      const fov = (fovDeg ?? 46) * Math.PI / 180;
      pxPerM.value = 0.5 * viewH / Math.tan(fov / 2);
      // main.ts calls this immediately before `paintSky` in the same frame,
      // so the sample is one frame stale — far below the quantisation of the
      // colour itself.
      skyColourAlong(skyDir, skyCol);
      const skyBright = Math.min(1.5, (
        0.2126 * toLinear(skyCol.r) + 0.7152 * toLinear(skyCol.g) + 0.0722 * toLinear(skyCol.b)
      ) / MIDDAY_SKY_LUM);
      mat.emissive.copy(FALL_EMISSIVE).multiplyScalar(skyBright);
      mistMat.color.copy(MIST_COLOUR).multiplyScalar(skyBright);
      let k = 0;
      for (const f of feet) {
        const d = Math.hypot(f.x - eye.x, f.y - eye.y, f.z - eye.z);
        if (d > MIST_RANGE) continue;
        // thins away over the last third rather than switching off at a line
        const fade = 1 - smoothstep(MIST_RANGE * 0.62, MIST_RANGE, d);
        // a bigger fall throws more of it
        const puffs = Math.min(14, 3 + Math.floor(Math.log(1 + f.power / 4000) * 3));
        for (let j = 0; j < puffs && k < MIST_CAP; j++, k++) {
          const h1 = hash01(f.seed, j, 3), h2 = hash01(f.seed, j, 5), h3 = hash01(f.seed, j, 7);
          // boil upward on the world clock and fold back — pure in tSec, so
          // two machines draw the same mist at the same instant
          const u = ((tSec * (0.06 + h3 * 0.05) + h1) % 1);
          const rise = u * (14 + f.power / 26000);
          const spread = 5 + u * 16 + h2 * 6;
          const a = h1 * Math.PI * 2 + u * 1.6;
          _p.set(f.x + Math.cos(a) * spread * (0.4 + h2 * 0.6),
                 f.y + rise + 2,
                 f.z + Math.sin(a) * spread * (0.4 + h2 * 0.6));
          // real metres, and it fades out over the last third of its range so
          // the cloud thins away instead of switching off
          const size = (7 + u * 11 + h3 * 4) * fade;
          _q.copy(eyeFacing(eye, _p));
          _s.set(size, size, size);
          _m.compose(_p, _q, _s);
          mist.setMatrixAt(k, _m);
        }
      }
      mist.count = k;
      mist.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      geo.dispose(); mat.dispose();
      mist.geometry.dispose(); (mist.material as THREE.Material).dispose();
    },
  };
}

/**
 * A soft blob of spray — `structures.ts`' smoke trick, offset radial falloffs
 * straight into a DataTexture so the module needs no DOM and builds the same
 * headless. Spray is finer and rounder than woodsmoke, so this is one broad
 * core with three small satellites rather than four equal lumps.
 */
function mistTexture(): THREE.DataTexture {
  const s = 64;
  const blobs: Array<[number, number, number]> = [
    [s * 0.50, s * 0.52, s * 0.34],
    [s * 0.36, s * 0.40, s * 0.19],
    [s * 0.63, s * 0.44, s * 0.17],
    [s * 0.52, s * 0.66, s * 0.20],
  ];
  const data = new Uint8Array(s * s * 4);
  for (let y = 0; y < s; y++) for (let x = 0; x < s; x++) {
    let a = 0;
    for (const [bx, by, br] of blobs) {
      const t = Math.max(0, 1 - Math.hypot(x - bx, y - by) / br);
      a += t * t * 0.55;
    }
    const k = (y * s + x) * 4;
    data[k] = data[k + 1] = data[k + 2] = 255;
    data[k + 3] = Math.min(1, a) * 255;
  }
  const tex = new THREE.DataTexture(data, s, s);
  tex.magFilter = tex.minFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

const _look = new THREE.Matrix4(), _up = new THREE.Vector3(0, 1, 0);
const _fq = new THREE.Quaternion();
/** Face the eye, keeping the puff upright — a billboard, not a free spin. */
function eyeFacing(eye: THREE.Vector3, at: THREE.Vector3): THREE.Quaternion {
  _look.lookAt(eye, at, _up);
  return _fq.setFromRotationMatrix(_look);
}
